import React, { useContext } from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Typography } from '@comparethemarketau/manor-typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faQuestionCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';

import { SectionTitle } from '../common/section_title';
import { ContentCard } from '../common/content_card';
import { ContentTitleText } from '../common/content_title_text';
import { withQuoteFlatten } from '../../products/products/common/with_quote_flatten';
import { Separator } from '../../products/products/common/separator';

const useStyles = createUseStyles((theme) => ({
    container: {
        marginBottom: theme.spacing[40]
    },
    providerInfo: {
        marginTop: theme.spacing[12]
    },
    separatorBox: {
        marginTop: theme.spacing[16],
        marginBottom: theme.spacing[16]
    },
    subTitle: {
        fontSize: theme.fontSize.sm,
        fontWeight: theme.fontWeight.normal,
        color: theme.colors.grey700,
        marginBottom: theme.spacing[12]
    },
    valueType: {
        fontWeight: theme.fontWeight.black,
        color: 'black'
    },
    icon: {
        marginRight: theme.spacing[8]
    },
    label: {
        height: '23px',
        width: 'max-content',
        fontStyle: 'normal',
        fontWeight: theme.fontWeight.black,
        fontSize: theme.fontSize.xs,
        lineHeight: theme.lineHeight.normal,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        borderRadius: '0.4rem',
        padding: '0.4rem 0.8rem',
        margin: '0.4rem 0',
        gap: theme.spacing[8],
        extend: ({ value }) => {
            switch (value) {
                case 'YES_DEFAULT':
                case 'YES': return { color: theme.colors.hero500, background: theme.colors.hero50 };
                case 'NO': return { color: theme.colors.error500, background: theme.colors.error50 };
                case 'MAYBE': return { color: theme.colors.grey800, background: theme.colors.grey200 };
                default: return {};
            }
        }
    }
}));

const getValueLabel = (value, description) => {
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme, value });

    switch (value) {
        case 'NO':
            return (
                <div className={classes.label}>
                    <FontAwesomeIcon icon={faTimesCircle} className={classes.icon} />
                    {description}
                </div>
            );
        case 'YES_DEFAULT':
            return (
                <div className={classes.label}>
                    <FontAwesomeIcon icon={faCheckCircle} className={classes.icon} />
                    {description}
                </div>
            );
        case 'YES':
            return (
                <div className={classes.label}>
                    <FontAwesomeIcon icon={faCheckCircle} className={classes.icon} />
                    {description}
                </div>
            );
        case 'MAYBE':
            return (
                <div className={classes.label}>
                    <FontAwesomeIcon icon={faQuestionCircle} className={classes.icon} />
                    {description}
                </div>
            );
        default:
            return '';
    }
};

const _VehicleValue = ({ className, config, providerName, vehicleMarketValue, vehicleMarketValueDescription, vehicleAgreedValue, vehicleAgreedValueDescription }) => {
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme });
    const { id, label } = config;

    return (
        <div id={id} className={classNames('test-policyProduct-vehicleValue', classes.container, className)}>
            <SectionTitle label={label} />
            <ContentCard>
                <ContentTitleText title="Market vs. Agreed Value" />
                <Typography component="div" className={classes.providerInfo}>
                    For
                    {' '}
                    {providerName}
                    {' '}
                    policies, your vehicle will be insured for
                    {' '}
                    <strong>
                        {' '}
                        {vehicleMarketValue === 'YES_DEFAULT' ? 'Market' : 'Agreed'}
                        {' '}
                        value
                        {' '}
                    </strong>
                    {' '}
                    by default.
                </Typography>
                <Separator type="horizontal" className={classes.separatorBox} />
                <Typography className={classNames(classes.subTitle)}>
                    You may be able to customise your insured value when tailoring your quote on the provider’s site
                </Typography>
                {getValueLabel(vehicleMarketValue, vehicleMarketValueDescription)}
                <Typography className={classNames(classes.subTitle)}>
                    <strong className={classes.valueType}> Market Value </strong>
                    - The value of the vehicle will be determined at the time of claim.
                    This is typically the reasonable cost to replace your car with one of the same make, model,
                    age and condition as the car at the time of total loss or damage.
                </Typography>
                {getValueLabel(vehicleAgreedValue, vehicleAgreedValueDescription)}
                <Typography className={classNames(classes.subTitle)}>
                    <strong className={classes.valueType}> Agreed Value </strong>
                    - The value of the vehicle is specified at the beginning of each term of insurance.
                    This is the amount the insurer agrees to pay for the vehicle if it is deemed a total loss.
                    You may be able to specify the agreed value when finalising your quote on the provider's site.
                </Typography>
            </ContentCard>
        </div>
    );
};

_VehicleValue.propTypes = {
    className: PropTypes.string,
    config: PropTypes.object.isRequired,
    providerName: PropTypes.string,
    vehicleMarketValue: PropTypes.string,
    vehicleMarketValueDescription: PropTypes.string,
    vehicleAgreedValue: PropTypes.string,
    vehicleAgreedValueDescription: PropTypes.string
};

const VehicleValue = withQuoteFlatten(_VehicleValue);

export { VehicleValue };
